// src/components/ui/AnimatedCharacter.js
import React, { useEffect, useState, useRef } from 'react';
import { motion, useAnimate } from 'framer-motion';

/**
 * AnimatedCharacter - Animated logo character with moving eyes and body squish
 * 
 * The character looks at different target positions and squishes its body
 * in the direction it's looking. Eyes scale and move based on viewing angle.
 * 
 * @param {Object} props
 * @param {Array} props.lookAtSequence - Array of {x, y, duration, type} positions
 * @param {boolean} props.staticMode - If true, eyes stay centered and only special animations play
 * @param {Function} props.onStepChange - Callback fired when animation step changes, receives current target
 * @param {string} props.className - Additional CSS classes
 */
const AnimatedCharacter = ({ 
  lookAtSequence = [],
  staticMode = false,
  onStepChange,
  className = "" 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentRotationRef = useRef(0); // Use ref instead of state to avoid re-renders
  const [bodyScope, animateBody] = useAnimate();
  const [leftEyeScope, animateLeftEye] = useAnimate();
  const [rightEyeScope, animateRightEye] = useAnimate();
  const animationIdRef = useRef(0);

  // Calculate the angle and distance from center to target
  const calculateEyeTransforms = (targetX, targetY, containerRect) => {
    // In static mode, always return centered eyes
    if (staticMode) {
      return {
        leftEye: { x: 0, y: 0, scaleY: 1, transformOrigin: 'center center' },
        rightEye: { x: 0, y: 0, scaleY: 1, transformOrigin: 'center center' },
        angle: 0
      };
    }

    if (!containerRect) return { 
      leftEye: { x: 0, y: 0, scaleY: 1, transformOrigin: 'center center' },
      rightEye: { x: 0, y: 0, scaleY: 1, transformOrigin: 'center center' },
      angle: 0 
    };
    
    // Get center of the character
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;
    
    // Calculate angle to target
    const deltaX = targetX - centerX;
    const deltaY = targetY - centerY;
    const angle = Math.atan2(deltaY, deltaX);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Base eye movement - calculate horizontal and vertical independently
    // ADJUST THESE VALUES to control how far eyes move:
    const maxHorizontalDistance = 10; // Maximum pixels eyes can move horizontally
    const maxVerticalDistance = 25; // Maximum pixels eyes can move vertically
    const horizontalDivisor = 10; // Decrease for more horizontal movement
    const verticalDivisor = 8; // Decrease for more vertical movement
    
    // Calculate horizontal movement based on horizontal distance only
    const horizontalDistance = Math.abs(deltaX);
    const horizontalMove = Math.min(horizontalDistance / horizontalDivisor, maxHorizontalDistance);
    const baseX = (deltaX > 0 ? 1 : -1) * horizontalMove;
    
    // Calculate vertical movement based on vertical distance only
    const verticalDistance = Math.abs(deltaY);
    const verticalMove = Math.min(verticalDistance / verticalDivisor, maxVerticalDistance);
    const baseY = (deltaY > 0 ? 1 : -1) * verticalMove;
    
    // Calculate vertical scaling (eyes get shorter when looking up/down)
    // Smooth scaling based on vertical distance
    const minScale = 0.6; // Minimum scale when fully looking up/down
    const deadZone = 60; // No scaling within this vertical distance
    const maxScaleDistance = 170; // Distance at which minimum scale is reached
    
    let scaleY = 1;
    let transformOrigin = 'center center';
    
    const absVerticalDistance = Math.abs(deltaY);
    
    if (absVerticalDistance > deadZone) {
      // Calculate how far beyond the dead zone we are (0 to 1)
      const scaleFactor = Math.min((absVerticalDistance - deadZone) / (maxScaleDistance - deadZone), 1);
      // Interpolate between 1.0 (full) and minScale (0.6)
      scaleY = 1 - (scaleFactor * (1 - minScale));
      
      // Set transform origin based on direction
      if (deltaY > 0) { // Looking down
        transformOrigin = 'center top'; // Shrink from top
      } else { // Looking up
        transformOrigin = 'center bottom'; // Shrink from bottom
      }
    }
    
    // Eyes get closer together when looking at distant objects
    // Horizontal compression based on distance
    const compressionFactor = Math.min(distance / 1000, 1) * 0.15; // Up to 15% closer
    
    // Left eye moves slightly right, right eye moves slightly left
    const leftEyeX = baseX + (compressionFactor * 20);
    const rightEyeX = baseX - (compressionFactor * 20);
    
    return {
      leftEye: { 
        x: leftEyeX, 
        y: baseY, 
        scaleY,
        transformOrigin 
      },
      rightEye: { 
        x: rightEyeX, 
        y: baseY, 
        scaleY,
        transformOrigin 
      },
      angle
    };
  };

  // Calculate body squish based on eye direction
  const calculateBodySquish = (angle) => {
    // In static mode, no squish
    if (staticMode) {
      return { scaleX: 1, scaleY: 1, translateX: 0, translateY: 0 };
    }
    
    // No squish - body stays at normal scale
    return { scaleX: 1, scaleY: 1, translateX: 0, translateY: 0 };
  };

  // Special animations that can be triggered
  const performSpecialAnimation = async (animationType) => {
    switch (animationType) {
      case 'bouncy-spin':
        // 360 degree spin with bounce
        await animateBody(
          bodyScope.current,
          { rotate: 360 },
          { duration: 1.4, ease: [0.68, -0.8, 0.265, 1.8] } // Bouncy easing
        );
        // Reset rotation
        await animateBody(
          bodyScope.current,
          { rotate: 0 },
          { duration: 0 }
        );
        break;
        
      case 'blink':
        // Eyes scale down to 0 and back
        await Promise.all([
          animateLeftEye(
            leftEyeScope.current,
            { scaleX: 1, scaleY: 0.1 },
            { duration: 0.1, ease: 'easeIn' }
          ),
          animateRightEye(
            rightEyeScope.current,
            { scaleX: 1, scaleY: 0.1 },
            { duration: 0.1, ease: 'easeIn' }
          )
        ]);
        // Eyes back to normal
        await Promise.all([
          animateLeftEye(
            leftEyeScope.current,
            { scaleX: 1, scaleY: 1 },
            { duration: 0.15, ease: 'easeOut' }
          ),
          animateRightEye(
            rightEyeScope.current,
            { scaleX: 1, scaleY: 1 },
            { duration: 0.15, ease: 'easeOut' }
          )
        ]);
        break;
        
      case 'partial-spin':
        // Rotate by one "tooth" (40 degrees) - accumulates each time
        const newRotation = currentRotationRef.current + 40;
        await animateBody(
          bodyScope.current,
          { rotate: newRotation },
          { 
            duration: 0.75, 
            ease: [0.68, -0.8, 0.265, 1.8] // Bouncy back easing
          }
        );
        currentRotationRef.current = newRotation;
        break;
        
      default:
        break;
    }
  };

  useEffect(() => {
    if (lookAtSequence.length === 0) return;

    // Increment animation ID - this invalidates all previous animations
    animationIdRef.current += 1;
    const myAnimationId = animationIdRef.current;

    const animateToTarget = async () => {
      // ADDED: Check that all refs are hydrated before attempting any animations
      if (!bodyScope.current || !leftEyeScope.current || !rightEyeScope.current) {
        console.warn('AnimatedCharacter: Refs not yet hydrated, skipping animation');
        return;
      }

      // Check if this animation is still current
      if (animationIdRef.current !== myAnimationId) return;

      const target = lookAtSequence[currentIndex];
      
      // Notify parent of step change
      if (onStepChange) {
        onStepChange(target, currentIndex);
      }
      
      // In static mode, skip eye/body movement and only do special animations
      if (staticMode) {
        // If this step has a pause, wait before moving to next
        if (target.pause) {
          await new Promise(resolve => setTimeout(resolve, target.pause));
        }
        
        // Check if still current after pause
        if (animationIdRef.current !== myAnimationId) return;
        
        // If this step has a special animation, perform it
        if (target.specialAnimation) {
          await performSpecialAnimation(target.specialAnimation);
          // Pause after special animation
          if (target.postAnimationPause) {
            await new Promise(resolve => setTimeout(resolve, target.postAnimationPause));
          }
        }
        
        // Check if still current before moving to next
        if (animationIdRef.current !== myAnimationId) return;
        
        // Move to next target
        setCurrentIndex((prevIndex) => (prevIndex + 1) % lookAtSequence.length);
        return;
      }
      
      // Normal mode: Get character container position
      const containerRect = bodyScope.current?.getBoundingClientRect();
      if (!containerRect) return;

      // Calculate eye transforms
      const transforms = calculateEyeTransforms(target.x, target.y, containerRect);
      
      // Calculate body squish
      const bodySquish = calculateBodySquish(transforms.angle);

      // Determine animation duration and easing based on type
      const duration = target.duration || 0.8;
      const ease = target.type === 'smooth-scan' ? 'linear' : 'easeInOut';

      // Animate eyes and body simultaneously
      await Promise.all([
        animateLeftEye(
          leftEyeScope.current,
          { 
            x: transforms.leftEye.x, 
            y: transforms.leftEye.y,
            scaleY: transforms.leftEye.scaleY
          },
          { duration, ease }
        ),
        animateRightEye(
          rightEyeScope.current,
          { 
            x: transforms.rightEye.x, 
            y: transforms.rightEye.y,
            scaleY: transforms.rightEye.scaleY
          },
          { duration, ease }
        ),
        animateBody(
          bodyScope.current,
          { 
            scaleX: bodySquish.scaleX,
            scaleY: bodySquish.scaleY,
            x: bodySquish.translateX,
            y: bodySquish.translateY
          },
          { duration, ease }
        )
      ]);

      // Check if still current after animations
      if (animationIdRef.current !== myAnimationId) return;

      // If this step has a pause, wait before moving to next
      if (target.pause) {
        await new Promise(resolve => setTimeout(resolve, target.pause));
      }
      
      // Check if still current after pause
      if (animationIdRef.current !== myAnimationId) return;
      
      // If this step has a special animation, perform it
      if (target.specialAnimation) {
        await performSpecialAnimation(target.specialAnimation);
        // Pause after special animation
        if (target.postAnimationPause) {
          await new Promise(resolve => setTimeout(resolve, target.postAnimationPause));
        }
      }
      
      // Final check if still current before moving to next
      if (animationIdRef.current !== myAnimationId) return;
      
      // Move to next target
      setCurrentIndex((prevIndex) => (prevIndex + 1) % lookAtSequence.length);
    };

    animateToTarget();

    // Cleanup: increment the counter to invalidate this animation
    return () => {
      // The next render will increment animationIdRef.current, 
      // which will cause this animation to see it's no longer current
    };
  }, [currentIndex, lookAtSequence, animateBody, animateLeftEye, animateRightEye, bodyScope, leftEyeScope, rightEyeScope, staticMode, onStepChange]);
    
  return (
    <div className={`relative ${className}`}>
      {/* Container for positioning */}
      <div className="relative w-full h-full">
        
        {/* Animated body */}
        <motion.div
          ref={bodyScope}
          className="absolute inset-0"
          style={{ transformOrigin: 'center center' }}
        >
          <img 
            src="/blobby-body.svg" 
            alt="" 
            className="w-full h-full"
            draggable="false"
          />
        </motion.div>

        {/* Animated left eye */}
        <motion.div
          ref={leftEyeScope}
          className="absolute inset-0"
          style={{ transformOrigin: 'center center' }}
        >
          <img 
            src="/blobby-left-eye.svg" 
            alt="" 
            className="w-full h-full"
            draggable="false"
          />
        </motion.div>

        {/* Animated right eye */}
        <motion.div
          ref={rightEyeScope}
          className="absolute inset-0"
          style={{ transformOrigin: 'center center' }}
        >
          <img 
            src="/blobby-right-eye.svg" 
            alt="" 
            className="w-full h-full"
            draggable="false"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default AnimatedCharacter;